import { getIntentDisplayLabel } from "@/components/StructuredSearch/config/intentLabels";
import type { IntentHighlightAnnotation } from "@/types/requests";

const INTENT_SIGNAL_DESCRIPTIONS: Record<string, string> = {
  strong_phrases: "strong phrase match",
  strong_words: "strong word match",
  weak_phrases: "weak phrase match (ranking signal)",
  weak_words: "weak word match (ranking signal only)",
};

export const formatIntentHighlightTooltip = (
  annotation: Pick<IntentHighlightAnnotation, "intent" | "signal">,
): string => {
  const label = getIntentDisplayLabel(annotation.intent);
  const { signal } = annotation;
  if (signal && signal in INTENT_SIGNAL_DESCRIPTIONS) {
    return `${label} — ${INTENT_SIGNAL_DESCRIPTIONS[signal]}`;
  }
  return label;
};

type PreviewSegment = { kind: "text"; content: string } | { kind: "highlight"; content: string };

/** True when the gap between highlights is only whitespace and/or punctuation. */
export const isMergeableHighlightGap = (text: string): boolean =>
  text.length === 0 || !/[a-zA-Z0-9]/.test(text);

const normalizeHighlightText = (text: string): string => text.trim().toLowerCase();

export const findHighlightAnnotation = (
  text: string,
  annotations?: IntentHighlightAnnotation[] | null,
): IntentHighlightAnnotation | undefined => {
  if (!annotations?.length) {
    return undefined;
  }

  const normalized = normalizeHighlightText(text);
  const exactMatch = annotations.find(
    annotation => normalizeHighlightText(annotation.text) === normalized,
  );
  if (exactMatch) {
    return exactMatch;
  }

  return annotations.find(annotation => {
    const annotationText = normalizeHighlightText(annotation.text);
    return normalized.includes(annotationText) || annotationText.includes(normalized);
  });
};

export const mergeHighlightedPreviewSegments = (preview: string): PreviewSegment[] => {
  const rawSegments: PreviewSegment[] = [];
  let lastIndex = 0;
  const emTagPattern = /<em>(.*?)<\/em>/gi;

  for (const match of preview.matchAll(emTagPattern)) {
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      rawSegments.push({ kind: "text", content: preview.slice(lastIndex, matchIndex) });
    }

    rawSegments.push({ kind: "highlight", content: match[1] });
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < preview.length) {
    rawSegments.push({ kind: "text", content: preview.slice(lastIndex) });
  }

  const mergedSegments: PreviewSegment[] = [];
  let index = 0;

  while (index < rawSegments.length) {
    const segment = rawSegments[index];
    if (segment.kind !== "highlight") {
      mergedSegments.push(segment);
      index += 1;
      continue;
    }

    let highlightContent = segment.content;
    index += 1;

    while (index < rawSegments.length - 1) {
      const gap = rawSegments[index];
      const nextHighlight = rawSegments[index + 1];
      if (
        gap.kind === "text" &&
        nextHighlight.kind === "highlight" &&
        isMergeableHighlightGap(gap.content)
      ) {
        highlightContent += gap.content + nextHighlight.content;
        index += 2;
        continue;
      }
      break;
    }

    mergedSegments.push({ kind: "highlight", content: highlightContent });
  }

  return mergedSegments;
};
